import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { StoreInfoCells, StoreInfoHead } from "./StoreInfoCells";
import { ExportButton, TotalsRow } from "./ReportTotals";
import { HISTORY_REPORTS, STORE_COLS } from "./ReportHistory";
const need = (k) => ({ label: `Needed for ${k === "gold" ? "Gold" : "Platinum"}`, total: "sum", get: (r) => (r.acts == null || r[k] == null ? null : Math.max(0, r[k] - r.acts)) });
const NUM_COLS = [...HISTORY_REPORTS.avb.cols.slice(1), need("gold"), need("platinum")];
import { Award, Loader2, Trash2, Upload } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api/verizon-sales`;
const auth = () => ({ headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } });
const monthLabel = (m) => new Date(`${m}-15T12:00:00`).toLocaleDateString("en-US", { month: "long", year: "numeric" });
const STATUS = {
  platinum: ["Platinum", "bg-emerald-100 text-emerald-700"],
  gold: ["Gold", "bg-amber-100 text-amber-700"],
  none: ["Not pacing", "bg-red-100 text-red-700"],
};

const Uploader = ({ onDone }) => {
  const [busy, setBusy] = useState(false);
  const [month, setMonth] = useState("");
  const upload = async (file) => {
    if (!file) return;
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    if (month) fd.append("month", month);
    try {
      const r = await axios.post(`${API}/admin/avb/upload`, fd, auth());
      toast.success(`${monthLabel(r.data.month)}: loaded ${r.data.rows} stores (${r.data.matched} matched to a Store ID)`);
      onDone(r.data.month);
    } catch (e) { toast.error(e.response?.data?.detail || "Upload failed"); } finally { setBusy(false); }
  };
  return (
    <div className="flex items-center gap-2 flex-wrap">
      <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} title="Month (optional - read from the file name)"
        className="h-9 px-2 rounded-md border border-slate-200 text-sm" data-testid="avb-upload-month" />
      <label className="inline-flex" data-testid="avb-upload">
        <input type="file" accept=".csv" className="hidden" onChange={(e) => { upload(e.target.files[0]); e.target.value = ""; }} data-testid="avb-upload-input" />
        <span className="inline-flex items-center gap-2 h-9 px-3 rounded-md bg-teal text-white text-sm font-medium cursor-pointer hover:bg-teal/90 transition-colors">
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Upload className="w-4 h-4" />}Upload month CSV
        </span>
      </label>
    </div>
  );
};

const Progress = ({ acts, gold, platinum }) => {
  if (acts == null || !platinum) return <span className="text-xs text-slate-400">Waiting on Verizon acts</span>;
  const pct = (v) => Math.min(100, (v / platinum) * 100);
  return (
    <div className="relative h-2 w-[8vw] min-w-[48px] rounded-full bg-slate-100" title={`${acts} of ${platinum}`}>
      <div className="absolute inset-y-0 left-0 rounded-full bg-teal transition-[width] duration-500" style={{ width: `${pct(acts)}%` }} />
      {gold && <div className="absolute -top-0.5 -bottom-0.5 w-0.5 bg-amber-500" style={{ left: `${pct(gold)}%` }} title={`Gold ${gold}`} />}
    </div>
  );
};

const Need = ({ acts, target }) => {
  if (acts == null || target == null) return <td className="py-2 px-3 text-right text-slate-400">—</td>;
  const left = Math.max(0, target - acts);
  return <td className={`py-2 px-3 text-right font-medium ${left ? "text-navy" : "text-emerald-600"}`}>{left || "Hit"}</td>;
};

const Row = ({ r, admin }) => {
  const st = STATUS[r.status];
  return (
    <tr className="border-b border-slate-100" data-testid={`avb-row-${r.door_tsp}`}>
      {admin && <StoreInfoCells r={r} />}
      <td className="py-2 px-3 font-medium text-navy">{r.store_id || "—"}</td>
      <td className="py-2 px-3 text-slate-600">{r.door_tsp}</td>
      <td className="py-2 px-3">{st ? <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${st[1]}`} data-testid={`avb-status-${r.door_tsp}`}>{st[0]}</span> : <span className="text-xs text-slate-400">—</span>}</td>
      <td className="py-2 px-3 text-right">{r.acts ?? "—"}</td>
      <td className="py-2 px-3 text-right">{r.pacing_acts ?? "—"}</td>
      <td className="py-2 px-3 text-right">{r.gold ?? "—"}</td>
      <td className="py-2 px-3 text-right">{r.platinum ?? "—"}</td>
      <Need acts={r.acts} target={r.gold} />
      <Need acts={r.acts} target={r.platinum} />
      <td className="py-2 px-3"><Progress acts={r.acts} gold={r.gold} platinum={r.platinum} /></td>
    </tr>
  );
};

const HEAD = ["Store ID", "TSP ID", "Status", "Acts", "Pacing", "Gold", "Platinum", "To Gold", "To Platinum", "Progress"];

export const AVBReport = ({ mode = "admin", canUpload = false }) => {
  const [d, setD] = useState(null);
  const [month, setMonth] = useState("");
  const load = useCallback((m) => axios.get(`${API}/${mode}/avb`, { ...auth(), params: m ? { month: m } : {} })
    .then((r) => { setD(r.data); setMonth(r.data.month || ""); }).catch(() => setD({ months: [], rows: [] })), [mode]);
  useEffect(() => { load(); }, [load]);
  const remove = async () => {
    if (!window.confirm(`Remove the ${monthLabel(month)} Activation Volume Bonus report?`)) return;
    await axios.delete(`${API}/admin/avb/${month}`, auth());
    toast.success("Month removed");
    load();
  };
  if (!d) return <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-teal" /></div>;
  if (mode === "dealer" && !d.rows.length) return null;
  return (
    <Card className="border-slate-200" data-testid="avb-report">
      <CardContent className="p-4 lg:p-6 space-y-4">
        <div className="flex items-start justify-between gap-3 flex-wrap">
          <div>
            <h3 className="font-heading font-semibold text-navy text-lg flex items-center gap-2"><Award className="w-5 h-5 text-teal" />Activation Volume Bonus</h3>
            <p className="text-sm text-slate-500">Verizon program, shown only for months Verizon runs it.{d.uploaded_at ? ` Updated ${new Date(d.uploaded_at).toLocaleString()}.` : " No month uploaded yet."}</p>
          </div>
          <div className="flex gap-2 flex-wrap">{canUpload && <Uploader onDone={load} />}<ExportButton title={`Activation Volume Bonus ${month}`} cols={[...STORE_COLS(mode === "admin"), HISTORY_REPORTS.avb.cols[0], ...NUM_COLS]} rows={d.rows} testId="avb-export-btn" /></div>
        </div>
        {d.months.length > 0 && (
          <div className="flex items-center gap-2 flex-wrap" data-testid="avb-months">
            {d.months.map((m) => (
              <Button key={m} size="sm" variant={m === month ? "default" : "outline"} onClick={() => load(m)} data-testid={`avb-month-${m}`}>{monthLabel(m)}</Button>
            ))}
            {canUpload && month && <Button size="sm" variant="ghost" className="text-red-600" onClick={remove} data-testid="avb-delete-month"><Trash2 className="w-4 h-4" /></Button>}
          </div>
        )}
        {d.rows.length > 0 && (
          <div className="overflow-hidden">
            <table className="vz-compact w-full">
              <thead><tr className="border-b border-slate-200 text-left text-slate-500">
                {mode === "admin" && <StoreInfoHead />}
                {HEAD.map((h, i) => <th key={h} className={`py-2 px-3 font-medium whitespace-nowrap ${i > 2 && i < 9 ? "text-right" : ""}`}>{h}</th>)}
              </tr></thead>
              <tbody>{d.rows.map((r) => <Row key={r.door_tsp} r={r} admin={mode === "admin"} />)}</tbody>
              <TotalsRow rows={d.rows} lead={mode === "admin" ? 5 : 3} label={`Total (${d.rows.length})`} cols={NUM_COLS} />
            </table>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
